import React, { createContext, useCallback, useContext, useState } from 'react';
import { FlyoverNotification, FlyoverType } from './FlyoverNotification';

// ──────────────────────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────────────────────

export interface FlyoverOptions {
	type: FlyoverType;
	message: string;
	/** Auto-dismiss delay in ms. 0 = never. Default: 4000 */
	autoDismissMs?: number;
}

interface FlyoverContextValue {
	showFlyover: (options: FlyoverOptions) => void;
	dismissFlyover: () => void;
}

// ──────────────────────────────────────────────────────────────────────────────
// Context
// ──────────────────────────────────────────────────────────────────────────────

const FlyoverContext = createContext<FlyoverContextValue | null>(null);

// ──────────────────────────────────────────────────────────────────────────────
// Provider
// ──────────────────────────────────────────────────────────────────────────────

export interface FlyoverProviderProps {
	children: React.ReactNode;
}

interface FlyoverState extends FlyoverOptions {
	visible: boolean;
}

export const FlyoverProvider: React.FC<FlyoverProviderProps> = ({ children }) => {
	const [state, setState] = useState<FlyoverState>({
		visible: false,
		type: FlyoverType.Info,
		message: '',
	});

	const showFlyover = useCallback((options: FlyoverOptions) => {
		setState({ ...options, visible: true });
	}, []);

	const dismissFlyover = useCallback(() => {
		setState((prev) => ({ ...prev, visible: false }));
	}, []);

	return (
		<FlyoverContext.Provider value={{ showFlyover, dismissFlyover }}>
			<FlyoverNotification
				type={state.type}
				message={state.message}
				visible={state.visible}
				onDismiss={dismissFlyover}
				autoDismissMs={state.autoDismissMs}
			/>
			{children}
		</FlyoverContext.Provider>
	);
};

// ──────────────────────────────────────────────────────────────────────────────
// Hook
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Returns helpers to show / dismiss the flyover notification.
 *
 * Must be used inside a <FlyoverProvider>.
 *
 * @example
 * const { showFlyover } = useFlyover();
 * showFlyover({ type: FlyoverType.Error, message: 'Failed to update setting. Try again.' });
 */
export const useFlyover = (): FlyoverContextValue => {
	const ctx = useContext(FlyoverContext);
	if (!ctx) {
		throw new Error('useFlyover must be used inside a <FlyoverProvider>');
	}
	return ctx;
};

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};
const noopFlyover: FlyoverContextValue = {
	showFlyover: noop,
	dismissFlyover: noop,
};

/**
 * Like useFlyover, but returns no-op functions when called outside a <FlyoverProvider>.
 * Use this in shared hooks/utilities where provider presence is not guaranteed (e.g. tests).
 */
export const useFlyoverSafe = (): FlyoverContextValue => {
	const ctx = useContext(FlyoverContext);
	return ctx ?? noopFlyover;
};

export { FlyoverType };
